function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useRef, useEffect } from 'react';
import { Box, TextInput } from 'grommet';
import styled from 'styled-components';
import PropTypes from 'prop-types';

// keyCode constants
const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;
const SPACEBAR = 32;
const StyledInput = styled(TextInput)`
  width: 50px;
  height: 50px;
  text-align: center;
  border-color: ${props => props.theme.global.colors[props.borderColor]};
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;
const Parent = styled(Box)`
  border: none !important;
  input {
    border: ${props => props.showBorder ? '1px solid #c6c6c6' : 'none'};
    border-bottom: ${props => props.showBorder ? undefined : '2px solid #c6c6c6'};
    background-color: ${props => props.showBorder ? '#f8f8f8' : undefined};
    border-radius: ${props => props.showBorder ? '5px' : '0px'};
    &:focus {
      border: ${props => props.showBorder ? '1px solid ' + props.theme.global.colors.brand : undefined};
      border-bottom: ${props => props.showBorder ? undefined : '2px solid ' + props.theme.global.colors.brand};
    }
  }
`;
const SingleInput = ({
  value,
  active,
  error,
  showBorder,
  ...rest
}) => {
  const InputRef = useRef();
  useEffect(() => {
    if (active && InputRef.current) {
      InputRef.current.focus();
      InputRef.current.select();
    }
  }, [active]);
  return /*#__PURE__*/React.createElement(Parent, {
    showBorder: showBorder
  }, /*#__PURE__*/React.createElement(StyledInput, _extends({
    borderColor: error ? 'status-critical' : 'border',
    autoComplete: "off",
    value: value,
    ref: InputRef
  }, rest)));
};
SingleInput.propTypes = {
  value: PropTypes.string,
  active: PropTypes.bool,
  error: PropTypes.string,
  showBorder: PropTypes.bool
};
const OtpInput = ({
  numInputs,
  value: initialValue,
  onChange,
  onBlur,
  showBorder,
  ...rest
}) => {
  const [activeInput, setActiveInput] = useState(0);
  const otp = initialValue.split('');
  const handleChange = e => {
    const {
      value
    } = e.target;
    otp[activeInput] = value[0].toUpperCase();
    onChange(otp.join(''));
  };
  const handleBlur = e => {
    setActiveInput(-1);
    onBlur(e);
  };
  const changeCodeAtFocus = value => {
    otp[activeInput] = value[0];
    onChange(otp.join(''));
  };
  const handleOnInput = e => {
    if (initialValue.length === numInputs || initialValue.length - 1 === activeInput) focusNextInput();else focusInput(initialValue.length + 1);
  };
  const focusInput = input => {
    const activeInput = Math.max(Math.min(numInputs - 1, input), 0);
    setActiveInput(activeInput);
  };
  const focusPrevInput = () => {
    focusInput(activeInput - 1);
  };
  const focusNextInput = () => {
    focusInput(activeInput + 1);
  };
  const handleOnKeyDown = e => {
    if (e.keyCode === BACKSPACE || e.key === 'Backspace') {
      e.preventDefault();
      changeCodeAtFocus('');
      focusPrevInput();
    } else if (e.keyCode === DELETE || e.key === 'Delete') {
      e.preventDefault();
      changeCodeAtFocus('');
    } else if (e.keyCode === LEFT_ARROW || e.key === 'ArrowLeft') {
      e.preventDefault();
      focusPrevInput();
    } else if (e.keyCode === RIGHT_ARROW || e.key === 'ArrowRight') {
      e.preventDefault();
      focusNextInput();
    } else if (e.keyCode === SPACEBAR || e.key === ' ' || e.key === 'Spacebar' || e.key === 'Space') {
      e.preventDefault();
    }
  };
  const handleOnPaste = e => {
    let text = e.clipboardData.getData('Text') ? e.clipboardData.getData('Text').split('') : [];
    for (let i = 0; i < numInputs; i++) {
      if (text[i]) otp[i] = text[i];
    }
    onChange(otp.join(''));
  };
  let inputs = [];
  for (let i = 0; i < numInputs; i++) {
    inputs.push( /*#__PURE__*/React.createElement(SingleInput, _extends({
      active: activeInput === i,
      key: i,
      onInput: handleOnInput,
      value: otp && otp[i] || '',
      onChange: handleChange,
      onKeyDown: handleOnKeyDown,
      onBlur: handleBlur,
      onFocus: () => setActiveInput(i),
      onPaste: handleOnPaste,
      showBorder: showBorder
    }, rest)));
  }
  return /*#__PURE__*/React.createElement(Parent, {
    direction: "row",
    gap: "medium"
  }, inputs);
};
OtpInput.propTypes = {
  numInputs: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  value: PropTypes.string,
  showBorder: PropTypes.bool
};
OtpInput.defaultProps = {
  numInputs: 4,
  onChange: () => {},
  onBlur: () => {},
  error: undefined,
  value: '',
  showBorder: false
};
export default OtpInput;